/**
 * Per-kind presentation for every surface that shows a notification — the
 * header dropdown, the inbox and a live toast.
 *
 * Maps a notification's envelope to its visual treatment (icon, medallion
 * tone, card density). Only the icon is looked up by kind, and it falls back to
 * the category's — so a kind this build has never seen still gets a sensible
 * row. Copy and the action come from the envelope as they are.
 * Pure and data-only so it can be unit-tested; the components map the
 * returned `tone`/`density` to Dō design tokens.
 */

import {
  ArrowDownTrayIcon,
  ArrowTrendingUpIcon,
  BellIcon,
  BoltIcon,
  ChartBarIcon,
  ChatBubbleLeftEllipsisIcon,
  CheckBadgeIcon,
  CheckCircleIcon,
  ClipboardDocumentListIcon,
  ExclamationTriangleIcon,
  FireIcon,
  FlagIcon,
  ShareIcon,
  ShieldCheckIcon,
  TrophyIcon,
  UserCircleIcon,
  UserPlusIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';

import { NotificationUtils } from '@personal-trainer-app/shared';

import type {
  ChallengeCompletionMeta,
  NotificationActor,
  NotificationCategory,
  NotificationItem,
  NotificationPrimaryAction,
  NotificationRespondAction,
  RawNotification,
} from '@personal-trainer-app/shared';
import type { ComponentType, SVGProps } from 'react';

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

/**
 * Medallion colour role. The icon shape carries the category, so colour stays
 * reserved for genuine alerts: `error` for failures, `neutral` zinc for
 * everything else. This keeps a single-kind feed from becoming a wall of one
 * hue, and keeps sakura/semantic colour out of decorative chrome.
 */
export type NotificationTone = 'neutral' | 'error';

/** Card layout density. `feature` is the tall celebration card. */
export type NotificationDensity = 'feature' | 'compact';

export interface NotificationMetaChip {
  label: string;
}

export interface NotificationPresentation {
  /** The envelope's `kind`. */
  displayType: string;
  tone: NotificationTone;
  density: NotificationDensity;
  Icon: IconComponent;
  /** The question a `respond` action asks, answered with Accept/Decline. */
  respond?: NotificationRespondAction;
  /** Single call-to-action: navigate, download or generate. */
  primaryAction?: NotificationPrimaryAction;
  /** Inline status chip, e.g. a challenge rank. */
  metaChip?: NotificationMetaChip;
}

const KIND_ICONS: Record<string, IconComponent> = {
  personal_record: TrophyIcon,
  goal_milestone: CheckBadgeIcon,
  challenge_completed: FlagIcon,
  challenge_goal_completed: FlagIcon,
  challenge_weekly_progress: ArrowTrendingUpIcon,
  challenge_invitation: FireIcon,
  resource_share: ShareIcon,
  shared_session_ready: UsersIcon,
  shared_session_copied: UsersIcon,
  friend_request: UserPlusIcon,
  friend_request_accepted: UsersIcon,
  client_request: UserPlusIcon,
  client_at_risk: ExclamationTriangleIcon,
  report_exported: ArrowDownTrayIcon,
  report_export_failed: ExclamationTriangleIcon,
  plan_generation_failed: ExclamationTriangleIcon,
  plan_review_pending: ShieldCheckIcon,
  plan_review_approved: CheckCircleIcon,
  plan_review_declined: ExclamationTriangleIcon,
  workout_reminder: BoltIcon,
};

const CATEGORY_ICONS: Record<NotificationCategory, IconComponent> = {
  achievement: CheckBadgeIcon,
  challenge: FireIcon,
  social: UsersIcon,
  coaching: ChatBubbleLeftEllipsisIcon,
  report: ChartBarIcon,
  training: ClipboardDocumentListIcon,
  // Settings → Account's own icon: where every one of them goes.
  account: UserCircleIcon,
};

// Singular human-readable label per kind, used in cluster summaries
// ("12 personal records"). All pluralise regularly with a trailing "s".
const KIND_LABELS: Record<string, string> = {
  personal_record: 'personal record',
  goal_milestone: 'goal milestone',
  challenge_completed: 'challenge result',
  challenge_goal_completed: 'challenge result',
  challenge_weekly_progress: 'challenge update',
  challenge_invitation: 'challenge invitation',
  resource_share: 'shared resource',
  shared_session_ready: 'joint session',
  friend_request: 'friend request',
  client_request: 'client request',
  client_at_risk: 'client alert',
  client_workout_completed: 'client workout',
  trainer_message: 'trainer message',
  report_exported: 'export',
  report_export_failed: 'failed export',
  weekly_goal_progress: 'goal report',
  weekly_training_digest: 'weekly digest',
  weekly_roster_report: 'roster report',
  workout_reminder: 'reminder',
};

function getMetaChip(
  notification: NotificationItem | RawNotification
): NotificationMetaChip | undefined {
  const { kind, meta } = NotificationUtils.getEnvelope(notification);

  if (kind !== 'challenge_completed' && kind !== 'challenge_goal_completed') {
    return undefined;
  }

  const stats = (meta as ChallengeCompletionMeta | null)?.completion_stats;
  const rank = stats?.final_rank ?? stats?.current_rank;

  return typeof rank === 'number' && stats
    ? { label: `#${rank} of ${stats.total_participants}` }
    : undefined;
}

export function getNotificationPresentation(
  notification: NotificationItem | RawNotification
): NotificationPresentation {
  const { kind, category, level, action } =
    NotificationUtils.getEnvelope(notification);

  // A celebration or a question gets the tall card; everything else is a line.
  const isFeature =
    category === 'achievement' ||
    action?.type === 'respond' ||
    kind === 'challenge_completed' ||
    kind === 'challenge_goal_completed';

  return {
    displayType: kind,
    tone: level === 'error' ? 'error' : 'neutral',
    density: isFeature ? 'feature' : 'compact',
    Icon: KIND_ICONS[kind] ?? CATEGORY_ICONS[category] ?? BellIcon,
    respond: action?.type === 'respond' ? action : undefined,
    primaryAction: action && action.type !== 'respond' ? action : undefined,
    metaChip: getMetaChip(notification),
  };
}

/** The person the notification is from or about, shown as an avatar. */
export function getNotificationSender(
  notification: NotificationItem | RawNotification
): NotificationActor | null {
  return NotificationUtils.getEnvelope(notification).actor;
}

/** Human-readable kind label for cluster summaries (e.g. "personal record"). */
export function getNotificationTypeLabel(displayType: string): string {
  return KIND_LABELS[displayType] ?? 'notification';
}
